import * as z from 'zod';

// Enums

// Provisioning lifecycle: pending_dns → pending_validation → issued → active,
// with `failed` as the terminal failure state (retry = re-PUT the same domain).
export const landingPageDomainStatusEnum = z.enum([
  'pending_dns',
  'pending_validation',
  'issued',
  'active',
  'failed',
]);
export type LandingPageDomainStatus = z.infer<
  typeof landingPageDomainStatusEnum
>;

// A record the customer must publish in their own DNS zone. One CNAME covers
// every published page of the workspace — there is no per-page DNS setup.
export const landingPageDomainDnsRecordSchema = z.object({
  name: z.string().nullable(),
  type: z.string().nullable(),
  value: z.string().nullable(),
});
export type LandingPageDomainDnsRecord = z.infer<
  typeof landingPageDomainDnsRecordSchema
>;

// 3. Read schema - all properties nullable (API response).
// GET always returns 200: every field is null while no domain is registered,
// so this one shape drives the whole settings screen.
export const readLandingPageDomainSchema = z.object({
  domain: z.string().nullable(),
  status: landingPageDomainStatusEnum.nullable(),
  error: z.string().nullable(),
  baseUrl: z.string().nullable(),
  requiredDnsRecords: z.array(landingPageDomainDnsRecordSchema).nullable(),
  createdAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime().nullable(),
});
export type ReadLandingPageDomain = z.infer<typeof readLandingPageDomainSchema>;

// At least three labels: an apex domain cannot carry a CNAME, so it would
// never activate. Same rule as the backend validation.
export const landingPageDomainHostnameSchema = z
  .string()
  .max(255, 'Domain must not exceed 255 characters')
  .regex(
    /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]*[a-z0-9])?\.){2,}[a-z]{2,}$/,
    'Must be a fully qualified subdomain, e.g. lp.example.com',
  );

// 5. Update schema (PUT) - registers, replaces, or retries (re-PUT of the
// same domain after `failed` restarts provisioning).
export const updateLandingPageDomainSchema = z.object({
  domain: landingPageDomainHostnameSchema,
});
export type UpdateLandingPageDomain = z.infer<
  typeof updateLandingPageDomainSchema
>;
